import React, { useContext, useState, useCallback } from "react";
import {
  AdminSection,
  SectionTitle,
  SectionSubtitle,
  GridContainer,
  GridItem,
  StyledTextField,
  StyledButton,
} from "./AdminFields.styles";
import { useDispatch, useSelector } from "react-redux";
import {
  setAcquirerId,
  setIsoID,
  setApplyChanges,
  setReset,
  setWasUsed,
  setAcquirerCurrencyId,
} from "./AdminFields.slice";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ListIcon from "@mui/icons-material/List";
import { InputAdornment, IconButton } from "@mui/material";
import AuthContext from "../../context/AuthContext";
import { useEffect } from "react";
import AdminFieldsModal from "./AdminFields.modal";

const AdminFields = (props) => {
  const { hideAquirerField = false, hideIsoField = false } = props;
  const dispatch = useDispatch();
  const ROLE_ADMIN = "admin";
  const [modalType, setModalType] = useState("acquirer");
  const [modalOpen, setModalOpen] = useState(false);
  const { acquirerId, isoID, wasUsed } = useSelector(
    (state) => state.adminFields
  );
  const {
    acquirerData: {
      isoID: acquirerDataIsoID,
      acquirerId: acquirerDataId,
      currency_code: acquirerDataCurrencyId,
    },
    userInfo: { "acquirer-rol-data": userRole },
  } = useContext(AuthContext);

  const hasAccess = (allowedRoles) => allowedRoles.includes(userRole);

  const setAdquirerData = useCallback(() => {
    if (!hideAquirerField && acquirerDataId) {
      dispatch(setAcquirerId(acquirerDataId));
      dispatch(setAcquirerCurrencyId(acquirerDataCurrencyId));
    }
    if (!hideIsoField && acquirerDataIsoID)
      dispatch(setIsoID(acquirerDataIsoID));
  }, [hideAquirerField, hideIsoField, acquirerDataId, acquirerDataCurrencyId, acquirerDataIsoID, dispatch]);

  useEffect(() => {
    if (acquirerDataId || acquirerDataIsoID || acquirerDataCurrencyId) {
      setAdquirerData();
    }
  }, [acquirerDataId, acquirerDataIsoID, acquirerDataCurrencyId, setAdquirerData]);

  const handleAcquirerIdChange = (event) => {
    dispatch(setIsoID(""));
    dispatch(setAcquirerId(event.target.value));
  };

  const handleIsoIDChange = (event) => {
    dispatch(setIsoID(event.target.value));
  };

  const handleReset = () => {
    setAdquirerData();
    dispatch(setReset(true));
    dispatch(setWasUsed(false));
  };

  const handleApply = () => {
    dispatch(setApplyChanges(true));
  };

  const handleOpenModalAcquirer = () => {
    setModalType("acquirer");
    setModalOpen(true);
  };

  const handleOpenModalISO = () => {
    setModalType("iso");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType(null);
  };

  const handleSelectAcquirer = (selectedAcquirer) => {
    dispatch(setAcquirerId(selectedAcquirer.acquirer_id));
    dispatch(setAcquirerCurrencyId(selectedAcquirer.currency_alpha3_code));
    dispatch(setIsoID(""));
    dispatch(setWasUsed(false));
  };

  const handleSelectISO = (selectedISO) => {
    dispatch(setIsoID(selectedISO.isoID));
  };

  if (!hasAccess(ROLE_ADMIN)) {
    return null;
  }

  return (
    <GridContainer>
      <GridItem xs={12}>
        <AdminSection elevation={0}>
          <GridContainer padding="20px 0 0 20px">
            <GridItem xs={12}>
              <SectionTitle variant="h5">Admin Fields</SectionTitle>
            </GridItem>
            <GridItem xs={12}>
              <SectionSubtitle>
                Special configuration for system administrators
              </SectionSubtitle>
            </GridItem>
          </GridContainer>

          <GridContainer container justifyContent="center" alignItems="center">
            {!hideAquirerField && (
              <GridItem xs={12} md={3}>
                <StyledTextField
                  key={`acquirer-field`}
                  fullWidth
                  label="Acquirer ID"
                  value={acquirerId}
                  onChange={handleAcquirerIdChange}
                  variant="outlined"
                  placeholder="Enter Adquirer ID"
                  InputLabelProps={{
                    shrink: Boolean(acquirerId),
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="open acquirer list"
                          onClick={handleOpenModalAcquirer}
                        >
                          <ListIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </GridItem>
            )}
            {!hideIsoField && (
              <GridItem xs={12} md={3}>
                <StyledTextField
                  key={`iso-field`}
                  disabled={!acquirerId}
                  fullWidth
                  label="ISO ID"
                  value={isoID}
                  onChange={handleIsoIDChange}
                  variant="outlined"
                  placeholder="Enter ISO ID"
                  InputLabelProps={{
                    shrink: Boolean(isoID),
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disabled={!acquirerId}
                          aria-label="open ISO list"
                          onClick={handleOpenModalISO}
                        >
                          <ListIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </GridItem>
            )}
            <GridItem xs={12} md={2}>
              <StyledButton
                className="reset-button"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
              >
                Reset
              </StyledButton>
            </GridItem>
            <GridItem xs={12} md={2}>
              <StyledButton
                className="apply-button"
                onClick={handleApply}
                startIcon={<CheckCircleIcon />}
              >
                {!wasUsed ? "Apply" : "Applied"}
              </StyledButton>
            </GridItem>
          </GridContainer>
        </AdminSection>
      </GridItem>

      {modalOpen && modalType && (
        <AdminFieldsModal
          type={modalType}
          open={modalOpen}
          onClose={handleCloseModal}
          onSelectAcquirer={handleSelectAcquirer}
          onSelectISO={handleSelectISO}
        />
      )}
    </GridContainer>
  );
};

export default AdminFields;
